import React from 'react';
import './App.css';

const VERSION_STRING = 'v1.0.0 - Custom Battleship Calculator';

// Utility function to convert coordinates to string key
const coordsToKey = (x, y) => `${x},${y}`;

// Shared Ko-fi Footer Component
const KofiFooter = ({ deviceInfo }) => {
    return (
        <footer className={`app-footer ${deviceInfo.isMobile ? 'mobile-footer' : 'desktop-footer'}`}>
            <a 
                href="https://ko-fi.com/badak" 
                target="_blank" 
                rel="noopener noreferrer"
                className="kofi-link"
            >
                <img 
                    src="https://storage.ko-fi.com/cdn/kofi2.png?v=3" 
                    alt="Ko-fi"
                    className="kofi-logo"
                />
                Buy me the next battle pass !! 😉
            </a>
        </footer>
    );
};

const naturalsUpTo = (n) => [...Array(n).keys()];

class Tile extends React.Component {
    render() {
        const isBest = this.props.best !== null && this.props.best[0] === this.props.x && this.props.best[1] === this.props.y;
        const { deviceInfo } = this.props;
        
        // Determine CSS classes based on tile state and device
        let className = 'boardTile';
        if (!this.props.valid) className += ' invalid';
        if (isBest) className += ' selected';
        if (deviceInfo?.isMobile) className += ' mobile-tile';
        if (deviceInfo?.isTouchDevice) className += ' touch-tile';
        
        // Add state-specific classes
        if (this.props.text === 'HIT') {
            className += ' hit';
        } else if (this.props.text === 'MISS') {
            className += ' miss';
        } else if (this.props.text === 'SUNK') {
            className += ' sunk';
        } else if (this.props.text === 'DEDUCTED_MISS') {
            className += ' deducted-miss';
        } else if (this.props.text === null && this.props.score !== undefined) {
            // Dynamic score-based gradient classes
            const score = this.props.score;
            const minScore = this.props.minScore || 0;
            const maxScore = this.props.maxScore || 100;
            
            // Calculate relative position (0-1) within the dynamic range
            const range = maxScore - minScore;
            const relativeScore = range > 0 ? (score - minScore) / range : 0;
            
            // Map to gradient classes based on relative position
            if (relativeScore <= 0.14) className += ' score-very-low';        // Bottom 14%
            else if (relativeScore <= 0.28) className += ' score-low';        // 14-28%
            else if (relativeScore <= 0.42) className += ' score-medium-low'; // 28-42%
            else if (relativeScore <= 0.56) className += ' score-medium';     // 42-56%
            else if (relativeScore <= 0.70) className += ' score-medium-high';// 56-70%
            else if (relativeScore <= 0.85) className += ' score-high';       // 70-85%
            else className += ' score-max';                                   // Top 15%
        }

    // decide text rotation: tutorial tiles show straight text, main grid tiles should
    // counter-rotate the text so it remains horizontal regardless of grid rotation.
    const textStyle = { display: 'inline-block' };
    const rotation = this.props.rotation || 0;
    if (!this.props.tutorial) textStyle.transform = `rotate(${-rotation}deg)`;

        return <div className={className}
            key={this.props.x + ',' + this.props.y}
            style={{
                fontSize: this.props.fontSize,
                opacity: this.props.opacity
            }}
            onClick={this.props.onClick}
        >
            <span style={textStyle}>
                {this.props.text === null ? this.props.score : 
                 this.props.text === 'DEDUCTED_MISS' ? 'D' : this.props.text}
            </span>
        </div>;
    }
}

// Calculate raw heatmap scores for display
function calculateDisplayScores(grid, gridSize, shipConfig, shipStatus) {
    const shipCounts = {};
    
    // Use actual ship status to get remaining ships
    if (shipStatus) {
        for (let size = 2; size <= 5; size++) {
            const status = shipStatus[size] || [];
            shipCounts[size] = status.filter(sunk => !sunk).length;
        }
    } else {
        for (let size = 2; size <= 5; size++) {
            shipCounts[size] = shipConfig[size] || 0;
        }
    }
    
    // Generate combined heatmap
    const combinedHeatmap = new Array(gridSize * gridSize).fill(0);
    
    // Determine total remaining ships and current HIT cells
    const remainingShipsTotal = Object.values(shipCounts).reduce((a, b) => a + b, 0);
    const hitCells = [];
    for (let y = 0; y < gridSize; y++) {
        for (let x = 0; x < gridSize; x++) {
            const key = coordsToKey(x, y);
            if (grid[key] === 'HIT') hitCells.push([x, y]);
        }
    }

    for (let size = 2; size <= 5; size++) {
        if (shipCounts[size] > 0) {
            const shipHeatmap = generateHeatmap(grid, gridSize, size, { remainingShipsTotal, hitCells });
            
            for (let i = 0; i < gridSize * gridSize; i++) {
                combinedHeatmap[i] += shipHeatmap[i] * shipCounts[size];
            }
        }
    }
    
    return combinedHeatmap;
}

function sampleShip(length, gridSize) {
    const x = Math.round(Math.random() * (gridSize - 1));
    const y = Math.round(Math.random() * (gridSize - 1));
    const direction = Math.random() < 0.5;
    const cells = [[x, y]];
    for (let i = 0; i < length - 1; i++) {
        const cell = cells[cells.length - 1];
        const newXY = direction ? [cell[0] + 1, cell[1]] : [cell[0], cell[1] + 1];
        cells.push(newXY);
    }
    return cells;
}

function generateLayout(gridSize, shipConfig) {
    const layout = {};
    const hitLocations = {};
    
    for (let size = 2; size <= 5; size++) {
        const count = shipConfig[size] || 0;
        for (let i = 0; i < count; i++) {
            let attempts = 0;
            const maxAttempts = 1000; // Prevent infinite loops
            
            while (attempts < maxAttempts) {
                const candidate = sampleShip(size, gridSize);
                let isAdmissible = true;
                
                // Check if ship fits within grid
                for (const cell of candidate) {
                    if (cell[0] >= gridSize || cell[1] >= gridSize) {
                        isAdmissible = false;
                        break;
                    }
                }
                
                if (!isAdmissible) {
                    attempts++;
                    continue;
                }
                
                // Check if ship overlaps with existing ships
                for (const cell of candidate) {
                    const key = coordsToKey(cell[0], cell[1]);
                    if (hitLocations[key] === true) {
                        isAdmissible = false;
                        break;
                    }
                }
                
                if (!isAdmissible) {
                    attempts++;
                    continue;
                }
                
                // Check adjacency rule - no ships can be next to each other
                for (const cell of candidate) {
                    const [cellX, cellY] = cell;
                    
                    // Check all 8 adjacent cells
                    for (let dx = -1; dx <= 1; dx++) {
                        for (let dy = -1; dy <= 1; dy++) {
                            if (dx === 0 && dy === 0) continue;
                            
                            const adjX = cellX + dx;
                            const adjY = cellY + dy;
                            
                            // Skip if out of bounds
                            if (adjX < 0 || adjX >= gridSize || adjY < 0 || adjY >= gridSize) continue;
                            
                            // Check if adjacent cell contains a ship
                            const adjKey = coordsToKey(adjX, adjY);
                            if (hitLocations[adjKey] === true) {
                                isAdmissible = false;
                                break;
                            }
                        }
                        if (!isAdmissible) break;
                    }
                    if (!isAdmissible) break;
                }
                
                if (isAdmissible) {
                    layout[`ship${size}_${i}`] = candidate;
                    for (const cell of candidate) {
                        const key = coordsToKey(cell[0], cell[1]);
                        hitLocations[key] = true;
                    }
                    break;
                }
                
                attempts++;
            }
            
            if (attempts >= maxAttempts) {
                console.warn(`Could not place ship of size ${size} after ${maxAttempts} attempts. Consider reducing ship count or increasing grid size.`);
                // Skip this ship if we can't place it
                break;
            }
        }
    }
    return layout;
}

// Heatmap Generator - adapted from Python Method 6
function generateHeatmap(grid, gridSize, shipSize, options = {}) {
    const { remainingShipsTotal = -1, hitCells = [] } = options;
    const hitSet = new Set(hitCells.map(([x, y]) => `${x},${y}`));
    const heatmap = new Array(gridSize * gridSize).fill(0);
    
    // Check horizontal placements
    for (let y = 0; y < gridSize; y++) {
        for (let x = 0; x <= gridSize - shipSize; x++) {
            let score = 0;
            let valid = true;
            
            // Check if this horizontal placement is valid
            for (let i = 0; i < shipSize; i++) {
                const cellKey = coordsToKey(x + i, y);
                const cellValue = grid[cellKey];
                if (cellValue === 'MISS' || cellValue === 'DEDUCTED_MISS' || cellValue === 'SUNK') {
                    valid = false;
                    break;
                }
                if (cellValue === 'HIT') {
                    score += 10; // Bonus for hitting existing hits
                } else if (cellValue === null || cellValue === undefined) {
                    score += 1; // Normal score for empty cells
                }
            }
            
            if (valid && score > 0) {
                // If there is exactly one ship remaining overall, then any candidate
                // placement for that ship must account for ALL current HIT cells on the grid.
                if (remainingShipsTotal === 1 && hitSet.size > 0) {
                    // Build candidate cells for this horizontal placement
                    const candidateCells = new Set();
                    for (let i = 0; i < shipSize; i++) {
                        candidateCells.add(`${x + i},${y}`);
                    }
                    // If any hit cell is not included in the candidate, skip it
                    let includesAllHits = true;
                    for (const h of hitSet) {
                        if (!candidateCells.has(h)) { includesAllHits = false; break; }
                    }
                    if (!includesAllHits) {
                        valid = false;
                    }
                }
            }

            if (valid && score > 0) {
                // Add score to all cells this ship would occupy
                for (let i = 0; i < shipSize; i++) {
                    const index = (y * gridSize) + (x + i);
                    heatmap[index] += score > 10 ? 100 : 1; // High bonus if hits existing
                }
            }
        }
    }
    
    // Check vertical placements
    for (let x = 0; x < gridSize; x++) {
        for (let y = 0; y <= gridSize - shipSize; y++) {
            let score = 0;
            let valid = true;
            
            // Check if this vertical placement is valid
            for (let i = 0; i < shipSize; i++) {
                const cellKey = coordsToKey(x, y + i);
                const cellValue = grid[cellKey];
                if (cellValue === 'MISS' || cellValue === 'DEDUCTED_MISS' || cellValue === 'SUNK') {
                    valid = false;
                    break;
                }
                if (cellValue === 'HIT') {
                    score += 10; // Bonus for hitting existing hits
                } else if (cellValue === null || cellValue === undefined) {
                    score += 1; // Normal score for empty cells
                }
            }
            
            if (valid && score > 0) {
                // If exactly one ship remains, ensure this vertical candidate includes all HIT cells
                if (remainingShipsTotal === 1 && hitSet.size > 0) {
                    const candidateCells = new Set();
                    for (let i = 0; i < shipSize; i++) {
                        candidateCells.add(`${x},${y + i}`);
                    }
                    let includesAllHits = true;
                    for (const h of hitSet) {
                        if (!candidateCells.has(h)) { includesAllHits = false; break; }
                    }
                    if (!includesAllHits) {
                        valid = false;
                    }
                }
            }

            if (valid && score > 0) {
                // Add score to all cells this ship would occupy
                for (let i = 0; i < shipSize; i++) {
                    const index = ((y + i) * gridSize) + x;
                    heatmap[index] += score > 10 ? 100 : 1; // High bonus if hits existing
                }
            }
        }
    }
    
    // Penalize cells that are already hit or sunk (set to very low score)
    for (let y = 0; y < gridSize; y++) {
        for (let x = 0; x < gridSize; x++) {
            const cellKey = coordsToKey(x, y);
            if (grid[cellKey] === 'HIT' || grid[cellKey] === 'SUNK') {
                const index = y * gridSize + x;
                heatmap[index] = -10000; // Very low score for already hit cells
            }
        }
    }
    
    return heatmap;
}

// Probability-based strategy (Method 6 from Python)
function calculateBestMoveProbability(grid, gridSize, shipConfig, shipsDestroyed, shipStatus) {
    const shipCounts = {};
    
    // Use actual ship status instead of proportional scaling
    if (shipStatus) {
        // Count remaining ships from the actual tracking
        for (let size = 2; size <= 5; size++) {
            const status = shipStatus[size] || [];
            shipCounts[size] = status.filter(sunk => !sunk).length;
        }
    } else {
        // Fallback to original config if no ship status available
        for (let size = 2; size <= 5; size++) {
            const totalCount = shipConfig[size] || 0;
            shipCounts[size] = totalCount;
        }
        
        // If we know how many ships are destroyed, adjust remaining ships proportionally
        if (shipsDestroyed >= 0) {
            const totalShips = Object.values(shipConfig).reduce((a, b) => a + b, 0);
            const remainingShips = totalShips - shipsDestroyed;
            
            if (remainingShips <= 0) {
                return null; // All ships destroyed
            }
            
            // Scale down ship counts proportionally
            const scaleFactor = remainingShips / totalShips;
            for (let size = 2; size <= 5; size++) {
                shipCounts[size] = Math.ceil((shipConfig[size] || 0) * scaleFactor);
            }
        }
    }
    
    // Generate heatmaps for each ship type
    const combinedHeatmap = new Array(gridSize * gridSize).fill(0);
    
    // Compute remaining ships total and hit cells to pass as options
    const remainingShipsTotal = Object.values(shipCounts).reduce((a, b) => a + b, 0);
    const hitCells = [];
    for (let y = 0; y < gridSize; y++) {
        for (let x = 0; x < gridSize; x++) {
            const key = coordsToKey(x, y);
            if (grid[key] === 'HIT') hitCells.push([x, y]);
        }
    }

    for (let size = 2; size <= 5; size++) {
        if (shipCounts[size] > 0) {
            const shipHeatmap = generateHeatmap(grid, gridSize, size, { remainingShipsTotal, hitCells });
            
            // Add this ship's heatmap to the combined heatmap
            for (let i = 0; i < gridSize * gridSize; i++) {
                combinedHeatmap[i] += shipHeatmap[i] * shipCounts[size];
            }
        }
    }
    
    // Find the cell(s) with the highest score
    let maxScore = -Infinity;
    const bestCells = [];
    
    for (let y = 0; y < gridSize; y++) {
        for (let x = 0; x < gridSize; x++) {
            const cellKey = coordsToKey(x, y);
            if (grid[cellKey] === null || grid[cellKey] === undefined) { // Only consider empty cells
                const index = y * gridSize + x;
                const score = combinedHeatmap[index];
                
                if (score > maxScore) {
                    maxScore = score;
                    bestCells.length = 0; // Clear array
                    bestCells.push([x, y]);
                } else if (score === maxScore && score > 0) {
                    bestCells.push([x, y]);
                }
            }
        }
    }
    
    // If we have multiple best cells, use look-ahead simulation to break ties
    if (bestCells.length > 1) {
        const simulatedScores = new Map();
        const maxDepth = 0; // Maximum simulation depth
        
        function simulateNextBestMove(simGrid, depth = 0) {
            if (depth >= maxDepth) return 0;
            
            // Calculate heatmap for current simulation state
            const simHeatmap = calculateDisplayScores(simGrid, gridSize, shipConfig, shipStatus);
            let bestScore = Math.max(...simHeatmap.filter(score => score > 0));
            
            // If no valid moves left or score is 0, return 0
            if (bestScore <= 0) return 0;
            
            // For deeper simulations, return the best score without further branching
            if (depth > 0) return bestScore;
            
            // Find all cells with best score in simulation
            const simBestCells = [];
            for (let y = 0; y < gridSize; y++) {
                for (let x = 0; x < gridSize; x++) {
                    const index = y * gridSize + x;
                    const key = coordsToKey(x, y);
                    if (simGrid[key] === null && simHeatmap[index] === bestScore) {
                        simBestCells.push([x, y]);
                    }
                }
            }
            
            // If multiple best cells in simulation, recursively simulate each as a miss
            if (simBestCells.length > 1) {
                let bestFutureScore = 0;
                for (const [x, y] of simBestCells) {
                    const nextGrid = { ...simGrid };
                    nextGrid[coordsToKey(x, y)] = 'MISS';
                    const futureScore = simulateNextBestMove(nextGrid, depth + 1);
                    bestFutureScore = Math.max(bestFutureScore, futureScore);
                }
                return bestFutureScore;
            }
            
            return bestScore;
        }
        
        // Simulate each best cell as a miss and calculate future potential
        for (const [x, y] of bestCells) {
            const simGrid = { ...grid };
            simGrid[coordsToKey(x, y)] = 'MISS';
            const futureScore = simulateNextBestMove(simGrid);
            simulatedScores.set(coordsToKey(x, y), futureScore);
        }
        
        // Find the cell with the highest simulated future score
        let bestFutureScore = -Infinity;
        let bestCell = bestCells[0];
        
        for (const [x, y] of bestCells) {
            const futureScore = simulatedScores.get(coordsToKey(x, y));
            if (futureScore > bestFutureScore) {
                bestFutureScore = futureScore;
                bestCell = [x, y];
            }
        }
        
        return bestCell;
    }
    
    // If only one best cell, return it
    if (bestCells.length > 0) {
        return bestCells[0];
    }
    
    // Fallback: return any valid cell
    for (let y = 0; y < gridSize; y++) {
        for (let x = 0; x < gridSize; x++) {
            const cellKey = coordsToKey(x, y);
            if (grid[cellKey] === null || grid[cellKey] === undefined) {
                return [x, y];
            }
        }
    }
    
    return null;
}

class MainMap extends React.Component {
    constructor(props) {
        super(props);
        this.state = this.makeEmptyState();
        this.shortcutsHandler = this.shortcutsHandler.bind(this);
    }

    componentDidMount() {
        document.addEventListener('keydown', this.shortcutsHandler);
        this.doComputation(this.state.grid, this.state.shipsDestroyed);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.gridSize !== this.props.gridSize || 
            JSON.stringify(prevProps.shipConfig) !== JSON.stringify(this.props.shipConfig)) {
            // Reset state with new grid size and ship config
            const newState = this.makeEmptyState();
            this.setState({
                ...newState,
                gridSize: this.props.gridSize  // Explicitly update gridSize
            }, () => {
                this.doComputation(newState.grid, newState.shipsDestroyed);
            });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.shortcutsHandler);
    }

    makeEmptyGrid() {
        const grid = {};
        for (let y = 0; y < this.props.gridSize; y++)
            for (let x = 0; x < this.props.gridSize; x++)
                grid[coordsToKey(x, y)] = null;
        return grid;
    }

    makeEmptyState() {
        const probs = {};
        const scores = {};
        for (let y = 0; y < this.props.gridSize; y++)
            for (let x = 0; x < this.props.gridSize; x++) {
                probs[coordsToKey(x, y)] = 0.0;
                scores[coordsToKey(x, y)] = 0;
            }
        
        const shipLayout = generateLayout(this.props.gridSize, this.props.shipConfig);
        
        // Initialize ship tracking
        const shipStatus = {};
        for (let size = 2; size <= 5; size++) {
            const count = this.props.shipConfig[size] || 0;
            shipStatus[size] = Array(count).fill(false); // false = not sunk, true = sunk
        }
        
        return {
            mode: 'calculator',
            shipLayout,
            grid: this.makeEmptyGrid(),
            shipStatus,
            shipsDestroyed: -1,
            undoBuffer: [],
            probs,
            scores,
            best: [3, 4],
            cursorBelief: [0, 7],
            valid: true,
            observationProb: 1.0,
            lastComputationTime: -1,
            gridSize: this.props.gridSize,
            shipConfirmationPopups: [], // Add this for ship confirmation popups
            manuallyRemovedShips: [] // Track manually removed ship coordinates to prevent re-adding
        };
    }

    // Detect groups of adjacent HIT tiles and show confirmation popups
    detectAdjacentHitGroups(grid) {
        const visited = new Set();
        const hitGroups = [];
        
        for (let y = 0; y < this.state.gridSize; y++) {
            for (let x = 0; x < this.state.gridSize; x++) {
                const pos = `${x},${y}`;
                const cellKey = coordsToKey(x, y);
                if (visited.has(pos) || grid[cellKey] !== 'HIT') continue;
                
                // Find connected HIT cells
                const hitCells = this.findConnectedHitCells(grid, x, y, visited);
                
                // Only create popup for groups of 2 or more adjacent HIT tiles
                if (hitCells.length >= 2) {
                    hitGroups.push(hitCells);
                }
            }
        }
        
        // Create popup data for each group
        const popups = hitGroups.map((cells, index) => ({
            id: `ship_${index}`,
            cells: cells,
            coordinates: this.formatShipCoordinates(cells)
        }));
        
        return popups;
    }
    
    findConnectedHitCells(grid, startX, startY, visited) {
        const cells = [];
        const stack = [[startX, startY]];
        
        while (stack.length > 0) {
            const [x, y] = stack.pop();
            const pos = `${x},${y}`;
            
            if (visited.has(pos) || x < 0 || x >= this.state.gridSize || 
                y < 0 || y >= this.state.gridSize) {
                continue;
            }
            
            const cellKey = coordsToKey(x, y);
            const cellValue = grid[cellKey];
            if (cellValue !== 'HIT') {
                continue;
            }
            
            visited.add(pos);
            cells.push([x, y]);
            
            // Check 4 adjacent cells (not diagonal)
            stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
        }
        
        return cells;
    }
    
    formatShipCoordinates(cells) {
        // Sort cells to display coordinates in order
        const sortedCells = cells.sort((a, b) => a[1] === b[1] ? a[0] - b[0] : a[1] - b[1]);
        return sortedCells.map(([x, y]) => `${String.fromCharCode(65 + x)}${y + 1}`).join('-');
    }
    
    handleShipSunkConfirmation(shipId, isSunk) {
        if (isSunk) {
            // Find the popup data for this ship
            const popup = this.state.shipConfirmationPopups.find(p => p.id === shipId);
            if (popup) {
                // Mark all cells as SUNK
                const newGrid = { ...this.state.grid };
                for (const [x, y] of popup.cells) {
                    const cellKey = coordsToKey(x, y);
                    newGrid[cellKey] = 'SUNK';
                }
                
                // Remove this popup and store the cell coordinates to prevent re-adding
                const remainingPopups = this.state.shipConfirmationPopups.filter(p => p.id !== shipId);
                
                // Store the cell coordinates of manually removed ships to prevent re-adding
                const cellKey = popup.cells.map(([x, y]) => `${x},${y}`).sort().join('|');
                const manuallyRemovedShips = [...(this.state.manuallyRemovedShips || []), cellKey];
                
                this.setState({ 
                    grid: newGrid,
                    shipConfirmationPopups: remainingPopups,
                    manuallyRemovedShips: manuallyRemovedShips
                });
                
                // Restart computation after marking as sunk
                this.doComputation(newGrid, this.state.shipsDestroyed);
            }
        } else {
            // Just close the popup and store the cell coordinates to prevent re-adding
            const popup = this.state.shipConfirmationPopups.find(p => p.id === shipId);
            if (popup) {
                const remainingPopups = this.state.shipConfirmationPopups.filter(p => p.id !== shipId);
                const cellKey = popup.cells.map(([x, y]) => `${x},${y}`).sort().join('|');
                const manuallyRemovedShips = [...(this.state.manuallyRemovedShips || []), cellKey];
                
                this.setState({ 
                    shipConfirmationPopups: remainingPopups,
                    manuallyRemovedShips: manuallyRemovedShips
                });
            }
        }
    }
    
    renderShipConfirmationPopups() {
        if (this.state.shipConfirmationPopups.length === 0) return null;
        
        return (
            <div className="ship-confirmation-container">
                {this.state.shipConfirmationPopups.map((popup, index) => (
                    <div 
                        key={popup.id}
                        className="ship-confirmation-popup"
                    >
                        <span className="popup-text">
                            ⚓ Is ship at {popup.coordinates} sunk?
                        </span>
                        <button
                            className="popup-confirm-btn"
                            onClick={() => this.handleShipSunkConfirmation(popup.id, true)}
                        >
                            ✓ YES!
                        </button>
                    </div>
                ))}
            </div>
        );
    }

    // Detect and mark sunk ships automatically
    detectSunkShips(grid) {
        const newShipStatus = JSON.parse(JSON.stringify(this.state.shipStatus));
        let gridChanged = false;
        const newGrid = { ...grid };
        
        // Only look for HIT cells that aren't already part of a sunk ship
        // and only process each connected component once
        const visited = new Set();
        
        for (let y = 0; y < this.state.gridSize; y++) {
            for (let x = 0; x < this.state.gridSize; x++) {
                const pos = `${x},${y}`;
                // Only process HIT cells that haven't been visited yet
                // Skip SUNK cells to avoid reprocessing already detected ships
                if (visited.has(pos) || grid[[x, y]] !== 'HIT') continue;
                
                // Find connected ship segment
                const shipCells = this.findConnectedShipCells(grid, x, y, visited);
                
                if (shipCells.length >= 1) {
                    // Check if this is a complete ship (no adjacent HIT cells)
                    const isCompleteShip = this.isCompleteShip(grid, shipCells);
                    
                    if (isCompleteShip) {
                        // Mark all cells of this ship as SUNK
                        for (const [sx, sy] of shipCells) {
                            newGrid[[sx, sy]] = 'SUNK';
                            gridChanged = true;
                        }
                        
                        // Mark ship as sunk in status - only mark one ship of this size
                        const shipSize = shipCells.length;
                        if (newShipStatus[shipSize]) {
                            const firstAvailable = newShipStatus[shipSize].findIndex(sunk => !sunk);
                            if (firstAvailable !== -1) {
                                newShipStatus[shipSize][firstAvailable] = true;
                            }
                        }
                        
                        // Add DEDUCTED_MISS around the sunk ship
                        this.addDeductedMissesAroundShip(newGrid, shipCells);
                        gridChanged = true;
                    }
                }
            }
        }
        
        return { newGrid: gridChanged ? newGrid : grid, newShipStatus, gridChanged };
    }
    
    findConnectedShipCells(grid, startX, startY, visited) {
        const cells = [];
        const stack = [[startX, startY]];
        
        while (stack.length > 0) {
            const [x, y] = stack.pop();
            const pos = `${x},${y}`;
            
            if (visited.has(pos) || x < 0 || x >= this.state.gridSize || 
                y < 0 || y >= this.state.gridSize) {
                continue;
            }
            
            const cellKey = coordsToKey(x, y);
            const cellValue = grid[cellKey];
            // Only process HIT cells that aren't already part of a sunk ship
            if (cellValue !== 'HIT') {
                continue;
            }
            
            visited.add(pos);
            cells.push([x, y]);
            
            // Check 4 adjacent cells (not diagonal)
            stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
        }
        
        return cells;
    }
    
    // Calculate remaining ships based on current grid state
    calculateShipStatus(grid) {
        const shipStatus = {};
        
        // Initialize all ships as not sunk
        for (let size = 2; size <= 5; size++) {
            const count = this.props.shipConfig[size] || 0;
            shipStatus[size] = Array(count).fill(false);
        }
        
        // Find all sunk ships on the grid
        const visited = new Set();
        const sunkShips = [];
        
        for (let y = 0; y < this.state.gridSize; y++) {
            for (let x = 0; x < this.state.gridSize; x++) {
                const pos = `${x},${y}`;
                const cellKey = coordsToKey(x, y);
                if (visited.has(pos) || grid[cellKey] !== 'SUNK') continue;
                
                // Find connected sunk ship segment
                const shipCells = this.findConnectedSunkCells(grid, x, y, visited);
                
                if (shipCells.length >= 1) {
                    sunkShips.push(shipCells.length);
                }
            }
        }
        
        // Mark ships as sunk based on found sunk ships
        for (const shipSize of sunkShips) {
            if (shipStatus[shipSize]) {
                const firstAvailable = shipStatus[shipSize].findIndex(sunk => !sunk);
                if (firstAvailable !== -1) {
                    shipStatus[shipSize][firstAvailable] = true;
                }
            }
        }
        
        return shipStatus;
    }
    
    findConnectedSunkCells(grid, startX, startY, visited) {
        const cells = [];
        const stack = [[startX, startY]];
        
        while (stack.length > 0) {
            const [x, y] = stack.pop();
            const pos = `${x},${y}`;
            
            if (visited.has(pos) || x < 0 || x >= this.state.gridSize || 
                y < 0 || y >= this.state.gridSize) {
                continue;
            }
            
            const cellKey = coordsToKey(x, y);
            const cellValue = grid[cellKey];
            // Only process SUNK cells
            if (cellValue !== 'SUNK') {
                continue;
            }
            
            visited.add(pos);
            cells.push([x, y]);
            
            // Check 4 adjacent cells (not diagonal)
            stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
        }
        
        return cells;
    }

    addDeductedMissesAroundShip(grid, shipCells) {
        const shipSet = new Set(shipCells.map(([x, y]) => `${x},${y}`));
        
        for (const [x, y] of shipCells) {
            // Check all 8 adjacent cells
            for (let dx = -1; dx <= 1; dx++) {
                for (let dy = -1; dy <= 1; dy++) {
                    if (dx === 0 && dy === 0) continue;
                    
                    const nx = x + dx;
                    const ny = y + dy;
                    
                    if (nx >= 0 && nx < this.state.gridSize && 
                        ny >= 0 && ny < this.state.gridSize &&
                        !shipSet.has(`${nx},${ny}`)) {
                        const cellKey = coordsToKey(nx, ny);
                        if (grid[cellKey] === null || grid[cellKey] === undefined) {
                            grid[cellKey] = 'DEDUCTED_MISS';
                        }
                    }
                }
            }
        }
    }

    // Add deducted misses around all sunk ships (ships can't be adjacent)
    addDeductedMissesAroundSunkShips(grid) {
        const visited = new Set();
        
        for (let y = 0; y < this.state.gridSize; y++) {
            for (let x = 0; x < this.state.gridSize; x++) {
                const pos = `${x},${y}`;
                const cellKey = coordsToKey(x, y);
                if (visited.has(pos) || grid[cellKey] !== 'SUNK') continue;
                
                // Find all cells of this sunk ship
                const sunkShipCells = this.findConnectedSunkCells(grid, x, y, visited);
                
                // Add deducted misses around this sunk ship
                this.addDeductedMissesAroundShip(grid, sunkShipCells);
            }
        }
    }
    
    // Calculate deducted misses based on grid state and remaining ships
    calculateDeductedMisses(grid, shipStatus) {
        const newGrid = { ...grid };
        
        // First, remove all existing deducted misses
        for (let y = 0; y < this.state.gridSize; y++) {
            for (let x = 0; x < this.state.gridSize; x++) {
                const cellKey = coordsToKey(x, y);
                if (newGrid[cellKey] === 'DEDUCTED_MISS') {
                    newGrid[cellKey] = null;
                }
            }
        }
        
        // Get remaining ship sizes
        const remainingShips = [];
        for (let size = 2; size <= 5; size++) {
            const status = shipStatus[size] || [];
            const remaining = status.filter(sunk => !sunk).length;
            for (let i = 0; i < remaining; i++) {
                remainingShips.push(size);
            }
        }
        
        // If no ships remaining, mark all empty cells as deducted miss
        if (remainingShips.length === 0) {
            for (let y = 0; y < this.state.gridSize; y++) {
                for (let x = 0; x < this.state.gridSize; x++) {
                    const cellKey = coordsToKey(x, y);
                    if (newGrid[cellKey] === null || newGrid[cellKey] === undefined) {
                        newGrid[cellKey] = 'DEDUCTED_MISS';
                    }
                }
            }
            return newGrid;
        }
        
        // Add deducted misses around sunk ships (ships can't be adjacent)
        this.addDeductedMissesAroundSunkShips(newGrid);
        
        // Add deducted misses diagonally around miss tiles
        this.addDeductedMissesAroundMissTiles(newGrid);
        
        // For each empty cell, check if any remaining ship can be placed there
        for (let y = 0; y < this.state.gridSize; y++) {
            for (let x = 0; x < this.state.gridSize; x++) {
                const cellKey = coordsToKey(x, y);
                if (newGrid[cellKey] === null || newGrid[cellKey] === undefined) {
                    let canPlaceAnyShip = false;
                    
                    // Check if any remaining ship size can be placed at this position
                    for (const shipSize of remainingShips) {
                        if (this.canPlaceShipAtPosition(newGrid, x, y, shipSize)) {
                            canPlaceAnyShip = true;
                            break;
                        }
                    }
                    
                    // If no ship can be placed here, mark as deducted miss
                    if (!canPlaceAnyShip) {
                        newGrid[cellKey] = 'DEDUCTED_MISS';
                    }
                }
            }
        }
        
        return newGrid;
    }
    
    // Add deducted misses diagonally around hit tiles
    addDeductedMissesAroundMissTiles(grid) {
        for (let y = 0; y < this.state.gridSize; y++) {
            for (let x = 0; x < this.state.gridSize; x++) {
                const cellKey = coordsToKey(x, y);
                if (grid[cellKey] === 'HIT') {
                    // Check only the 4 diagonal cells around HIT tiles
                    const diagonalOffsets = [[-1, -1], [-1, 1], [1, -1], [1, 1]];
                    
                    for (const [dx, dy] of diagonalOffsets) {
                        const nx = x + dx;
                        const ny = y + dy;
                        
                        if (nx >= 0 && nx < this.state.gridSize && 
                            ny >= 0 && ny < this.state.gridSize) {
                            const adjCellKey = coordsToKey(nx, ny);
                            if (grid[adjCellKey] === null || grid[adjCellKey] === undefined) {
                                grid[adjCellKey] = 'DEDUCTED_MISS';
                            }
                        }
                    }
                }
            }
        }
    }

    // Check if a ship of given size can be placed such that it occupies position (x,y)
    canPlaceShipAtPosition(grid, x, y, shipSize) {
        // Vérifications de sécurité
        if (!this.state || !this.state.gridSize || !grid) {
            return false;
        }
        
        // Try all possible positions where this ship could be placed and still occupy (x,y)
        
        // Try horizontal placements - ship could start anywhere from (x-shipSize+1) to (x)
        for (let startX = Math.max(0, x - shipSize + 1); startX <= Math.min(x, this.state.gridSize - shipSize); startX++) {
            if (this.canPlaceShipInDirection(grid, startX, y, shipSize, true)) {
                return true;
            }
        }
        
        // Try vertical placements - ship could start anywhere from (y-shipSize+1) to (y)
        for (let startY = Math.max(0, y - shipSize + 1); startY <= Math.min(y, this.state.gridSize - shipSize); startY++) {
            if (this.canPlaceShipInDirection(grid, x, startY, shipSize, false)) {
                return true;
            }
        }
        
        return false;
    }
    
    // Check if a ship can be placed in a specific direction
    canPlaceShipInDirection(grid, startX, startY, shipSize, horizontal) {
        // Vérifications de sécurité
        if (!this.state || !this.state.gridSize || !grid) {
            return false;
        }
        
        // Check if ship fits within grid bounds
        const endX = horizontal ? startX + shipSize - 1 : startX;
        const endY = horizontal ? startY : startY + shipSize - 1;
        
        if (endX >= this.state.gridSize || endY >= this.state.gridSize) {
            return false;
        }
        
        // Check each cell of the potential ship placement
        for (let i = 0; i < shipSize; i++) {
            const x = horizontal ? startX + i : startX;
            const y = horizontal ? startY : startY + i;
            const cellKey = coordsToKey(x, y);
            const cellValue = grid[cellKey];
            
            // Ship cells must be either empty or HIT (for partially hit ships)
            if (cellValue !== null && cellValue !== undefined && cellValue !== 'HIT') {
                return false;
            }
        }
        
        return true;
    }

    doComputation(grid, shipsDestroyed) {
        const t0 = performance.now();
        
        // Calculate ship status based on current grid
        const newShipStatus = this.calculateShipStatus(grid);
        
        // Calculate deducted misses based on ship status and grid state
        const gridWithDeductedMisses = this.calculateDeductedMisses(grid, newShipStatus);
        
        // Calculate actual ships destroyed from the ship status
        let actualShipsDestroyed = 0;
        for (let size = 2; size <= 5; size++) {
            const status = newShipStatus[size] || [];
            actualShipsDestroyed += status.filter(sunk => sunk).length;
        }
        
        // Update state with calculated ship status and grid with deducted misses
        this.setState({ 
            grid: gridWithDeductedMisses,
            shipStatus: newShipStatus,
            shipsDestroyed: actualShipsDestroyed 
        });
        
        // Calculate raw scores for display
        const displayScores = calculateDisplayScores(gridWithDeductedMisses, this.state.gridSize, this.props.shipConfig, newShipStatus);
        
        // Find max score for normalization in colormap
        const maxScore = Math.max(...displayScores.filter(score => score > 0));
        
        let probs = [];
        let scores = [];
        for (let y = 0; y < this.state.gridSize; y++) {
            for (let x = 0; x < this.state.gridSize; x++) {
                const rawScore = displayScores[this.state.gridSize * y + x];
                const coordKey = coordsToKey(x, y);
                scores[coordKey] = rawScore;
                // Normalize for colormap only
                probs[coordKey] = maxScore > 0 ? Math.max(0, rawScore / maxScore) : 0;
            }
        }
        
        // Use heatmap strategy to determine best move
        const bestMove = calculateBestMoveProbability(gridWithDeductedMisses, this.state.gridSize, this.props.shipConfig, actualShipsDestroyed, newShipStatus);
        
        this.setState({ 
            probs, 
            scores,
            best: bestMove, 
            valid: true, 
            observationProb: 1.0, // No longer calculated
            computing: false 
        });
        
        const t1 = performance.now();
        this.setState({lastComputationTime: t1 - t0});
    }

    getGridStatistics(grid) {
        const hits = [];
        const misses = [];
        for (let y = 0; y < this.state.gridSize; y++) {
            for (let x = 0; x < this.state.gridSize; x++) {
                const cellKey = coordsToKey(x, y);
                const gridValue = grid[cellKey];
                if (gridValue === 'HIT' || gridValue === 'SUNK')
                    hits.push(this.state.gridSize * y + x);
                if (gridValue === 'MISS' || gridValue === 'DEDUCTED_MISS')
                    misses.push(this.state.gridSize * y + x);
            }
        }
        return {hits, misses};
    }

    copyToUndoBuffer() {
        this.setState({undoBuffer: [
            ...this.state.undoBuffer,
            {grid: this.state.grid, shipStatus: this.state.shipStatus, shipsDestroyed: this.state.shipsDestroyed, cursorBelief: this.state.cursorBelief},
        ]});
    }

    onClick(x, y, setAsHit) {
        const grid = { ...this.state.grid };
        const cellKey = coordsToKey(x, y);
        let gridValue = grid[cellKey];
        this.copyToUndoBuffer();

        if (this.state.mode === 'calculator') {
            switch (gridValue) {
                case 'MISS':
                    gridValue = 'HIT';
                    break;
                case 'HIT':
                    gridValue = null;
                    break;
                case 'SUNK':
                    gridValue = null;
                    break;
                default:
                    gridValue = setAsHit ? 'HIT' : 'MISS';
                    break;
            }
            grid[cellKey] = gridValue;
        } else {
            if (gridValue !== null)
                return;
            
            const arrayContains = (arr) => {
                for (const cell of arr)
                    if (cell[0] === x && cell[1] === y)
                        return true;
                return false;
            }
            
            let isHit = false;
            for (const shipKey of Object.keys(this.state.shipLayout)) {
                if (arrayContains(this.state.shipLayout[shipKey])) {
                    isHit = true;
                    break;
                }
            }
            
            gridValue = isHit ? 'HIT' : 'MISS';
            grid[cellKey] = gridValue;
        }
        
        this.setState({ grid, cursorBelief: [x, y] });
        
        // Detect adjacent HIT groups and show/update confirmation popups
        const shipPopups = this.detectAdjacentHitGroups(grid);
        
        // Filter existing popups to only keep ones that still have valid adjacent HIT groups
        // and haven't been manually removed
        const validExistingPopups = this.state.shipConfirmationPopups.filter(existingPopup => {
            return shipPopups.some(newPopup => this.isSameShipGroup(existingPopup.cells, newPopup.cells));
        });
        
        // Add new popups for groups that don't already have popups
        // and haven't been manually removed
        const newPopups = shipPopups.filter(newPopup => {
            // Check if this ship group was manually removed
            const cellKey = newPopup.cells.map(([x, y]) => `${x},${y}`).sort().join('|');
            const wasManuallyRemoved = this.state.manuallyRemovedShips && this.state.manuallyRemovedShips.includes(cellKey);
            
            if (wasManuallyRemoved) {
                return false;
            }
            
            return !validExistingPopups.some(existingPopup => this.isSameShipGroup(existingPopup.cells, newPopup.cells));
        });
        
        const allPopups = [...validExistingPopups, ...newPopups];
        this.setState({ shipConfirmationPopups: allPopups });
        
        // Always proceed with computation immediately, regardless of popups
        this.doComputation(grid, this.state.shipsDestroyed);
    }

    // Helper method to check if two ship groups are the same
    isSameShipGroup(cells1, cells2) {
        if (cells1.length !== cells2.length) return false;
        
        const cellsSet1 = new Set(cells1.map(([x, y]) => `${x},${y}`));
        const cellsSet2 = new Set(cells2.map(([x, y]) => `${x},${y}`));
        
        for (const cell of cellsSet1) {
            if (!cellsSet2.has(cell)) return false;
        }
        
        return true;
    }

    clearField() {
        const templateState = this.makeEmptyState();
        const newState = {};
        // Reset all grid-related state properties to ensure complete reset
        for (const name of [
            'shipLayout', 
            'grid', 
            'shipStatus', 
            'shipsDestroyed', 
            'undoBuffer', 
            'cursorBelief',
            'probs',
            'scores',
            'best',
            'valid',
            'observationProb',
            'lastComputationTime',
            'shipConfirmationPopups',
            'manuallyRemovedShips'
        ])
            newState[name] = templateState[name];
        this.setState(newState);
        this.doComputation(newState.grid, newState.shipsDestroyed);
    }

    undoLastMarking() {
        const undoBuffer = [...this.state.undoBuffer];
        if (undoBuffer.length === 0)
            return;
        const undoEntry = undoBuffer.pop();
        this.setState({
            grid: undoEntry.grid, 
            shipStatus: undoEntry.shipStatus || this.state.shipStatus,
            shipsDestroyed: undoEntry.shipsDestroyed, 
            cursorBelief: undoEntry.cursorBelief, 
            undoBuffer
        });
        this.doComputation(undoEntry.grid, undoEntry.shipsDestroyed);
    }

    reportMiss() {
        if (this.state.best !== null) {
            const bestCellKey = coordsToKey(this.state.best[0], this.state.best[1]);
            if (this.state.grid[bestCellKey] === null || this.state.grid[bestCellKey] === undefined) {
                this.onClick(...this.state.best);
            }
        }
    }

    reportHit() {
        if (this.state.best !== null) {
            const bestCellKey = coordsToKey(this.state.best[0], this.state.best[1]);
            if (this.state.grid[bestCellKey] === null || this.state.grid[bestCellKey] === undefined) {
                this.onClick(...this.state.best, true);
            }
        }
    }

    shortcutsHandler(evt) {
        if (evt.target?.getAttribute?.('data-stop-shortcuts'))
            return;

        if (evt.altKey)
            return;

        if (evt.ctrlKey) {
            if (evt.key.toLowerCase() === 'z' && evt.target?.tagName !== "INPUT") {
                evt.preventDefault();
                this.undoLastMarking();
            }
        } else {
            if (evt.code === 'KeyZ')
                this.reportMiss();
            if (evt.code === 'KeyX')
                this.reportHit();
        }
    }

    render() {
    // Removed unused deviceInfo destructuring to resolve ESLint warning

        // Contenu standard sans zoom container
        return <>
            {this.renderExternalShipStatus()}
            <div className="grid-container-inline">
                {this.renderActualMap()}
            </div>
            {this.renderShipConfirmationPopups()}
            
            <div className="controls-container">
                <div className="button-group">
                    {this.props.currentStage > 1 && (
                        <button className="reset-button" onClick={() => this.props.onStageChange(this.props.currentStage - 1)}>
                            ⬅️ Stage {this.props.currentStage - 1}
                        </button>
                    )}
                    <button className="reset-button" onClick={() => { this.clearField(); }}>Reset</button>
                    {this.props.currentStage < 11 && (
                        <button 
                            className="reset-button" 
                            onClick={() => {
                                const nextStage = this.props.currentStage + 1;
                                if (nextStage <= 11) {
                                    this.props.onStageChange(nextStage);
                                }
                            }}
                        >
                            Stage {this.props.currentStage + 1} ➡️
                        </button>
                    )}
                </div>
                
                <div style={{ textAlign: 'center' }}>
                    <span>Last recompute time: {this.state.lastComputationTime.toFixed(2)}ms</span>
                </div>
            </div>
        </>;
    }

    renderActualMap() {
        const { deviceInfo } = this.props;
        const rotation = this.props.rotation || 0;
        const { isMobile } = deviceInfo;
        const tileSize = isMobile ? 38 : 43.84;
        const rotationMargin = isMobile ? 50 : 60;  // Add margin to account for rotation
        const gridSize = this.state.gridSize;

        // Calculate score ranges for gradient
        let minScore = Infinity;
        let maxScore = -Infinity;
        
        if (this.state.scores) {
            for (let y = 0; y < gridSize; y++) {
                for (let x = 0; x < gridSize; x++) {
                    const cellKey = coordsToKey(x, y);
                    if (this.state.grid[cellKey] === null || this.state.grid[cellKey] === undefined) {
                        const score = this.state.scores[cellKey];
                        if (score !== undefined) {
                            minScore = Math.min(minScore, score);
                            maxScore = Math.max(maxScore, score);
                        }
                    }
                }
            }
        }

        if (minScore === Infinity) minScore = 0;
        if (maxScore === -Infinity) maxScore = 100;

        return (
            <div className="grid-wrapper" style={{ margin: `${rotationMargin}px` }}>
                <div className={`grid-container ${isMobile ? 'game-mobile' : ''}`}>
                    <div className="grid-rotated" style={{ transform: `rotate(${rotation}deg)` }}>
                        <div className="content-counter-rotated">
                            {/* Header row with column labels */}
                            <div className="grid-coord-row">
                                <div className="grid-coord-label" style={{ width: `${tileSize}px`, height: `${tileSize}px` }} /> {/* Empty corner */}
                                {naturalsUpTo(gridSize).map(x => (
                                    <div key={x} className="grid-coord-label" style={{ width: `${tileSize}px`, height: `${tileSize}px` }}>
                                        <span style={{ fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px', color: 'var(--white-foam)', display: 'inline-block', transform: `rotate(${-rotation}deg)` }}>
                                            {String.fromCharCode(65 + x)}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* Grid rows */}
                            {naturalsUpTo(gridSize).map(y => (
                                <div key={y} className="grid-coord-row">
                                    {/* Row label */}
                                    <div className="grid-coord-label" style={{ width: `${tileSize}px`, height: `${tileSize}px` }}>
                                        <span style={{ fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px', color: 'var(--white-foam)', display: 'inline-block', transform: `rotate(${-rotation}deg)` }}>
                                            {y + 1}
                                        </span>
                                    </div>
                                    
                                    {/* Grid tiles */}
                                    {naturalsUpTo(gridSize).map(x => {
                                        const cellKey = coordsToKey(x, y);
                                        const score = this.state.scores ? this.state.scores[cellKey] : 0;
                                        return (
                                            <Tile
                                                key={cellKey}
                                                x={x}
                                                y={y}
                                                text={this.state.grid[cellKey]}
                                                score={score}
                                                valid={this.state.valid}
                                                fontSize={isMobile ? '14px' : '14px'}
                                                deviceInfo={deviceInfo}
                                                best={this.state.best}
                                                onClick={() => this.onClick(x, y)}
                                                rotation={rotation}
                                                minScore={minScore}
                                                maxScore={maxScore}
                                                prob={this.state.probs ? this.state.probs[cellKey] : 0}
                                            />
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderShipStatus() {
        return (
            <div style={{ marginLeft: '20px', alignSelf: 'start' }}>
                <h3>Ships Remaining</h3>
                {[2, 3, 4, 5].map(size => {
                    const count = this.props.shipConfig[size] || 0;
                    if (count === 0) return null;
                    
                    const status = this.state.shipStatus[size] || [];
                    
                    return (
                        <div key={size} style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '14px', marginBottom: '5px' }}>
                                Size {size}: {status.filter(sunk => !sunk).length}/{count} remaining
                            </div>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                {Array(count).fill(0).map((_, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            width: `${size * 15}px`,
                                            height: '15px',
                                            backgroundColor: status[i] ? '#666' : '#4CAF50',
                                            border: '1px solid #333',
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '10px',
                                            color: 'white'
                                        }}
                                    >
                                        {status[i] && (
                                            <span style={{ 
                                                position: 'absolute',
                                                fontSize: '20px',
                                                color: 'red',
                                                fontWeight: 'bold'
                                            }}>
                                                ✗
                                            </span>
                                        )}
                                        {!status[i] && size}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderExternalShipStatus() {
        return (
            <div className="ship-status-top">
                <div className="ship-status-content">
                    <h3 className="ship-status-title">
                        🚢 Beach Defense Fleet
                    </h3>
                    {[2, 3, 4, 5].map(size => {
                        const count = this.props.shipConfig[size] || 0;
                        if (count === 0) return null;
                        
                        const status = this.state.shipStatus[size] || [];
                        
                        return (
                            <div key={size} className="ship-row">
                                <div className="ship-label">
                                    ⚓ Size {size}: {status.filter(sunk => !sunk).length}/{count} remaining
                                </div>
                                <div className="ship-visual">
                                    {Array(count).fill(0).map((_, i) => (
                                        <div
                                            key={i}
                                            className={`ship-icon ${status[i] ? 'sunk' : 'alive'}`}
                                            style={{
                                                width: `${size * 12}px`,
                                            }}
                                        >
                                            {status[i] && (
                                                <span className="ship-sunk-marker">✗</span>
                                            )}
                                            {!status[i] && size}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

class App extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            gridSize: 8,
            shipConfig: { 2: 1, 3: 1, 4: 1, 5: 0 },
            started: false,
            showConfig: false,
            showPresets: false,
            tutorialVisible: false,
            // tutorial internal state for interactive example
            tutorialStep: 0,
            tutorialGrid: null,
            tutorialGridSize: 3,
            tutorialShipCells: [],
            tutorialBest: null,
            tutorialProbs: {},
            tutorialScores: {},
            currentStage: 11
            ,
            gridRotationDeg: 0
        };
        
        // Preset configurations
        this.presets = {
            1: { gridSize: 3, shipConfig: { 2: 0, 3: 1, 4: 0, 5: 0 }, name: "Stage 1: Beginner" },
            2: { gridSize: 4, shipConfig: { 2: 2, 3: 0, 4: 0, 5: 0 }, name: "Stage 2: Easy" },
            3: { gridSize: 5, shipConfig: { 2: 2, 3: 1, 4: 0, 5: 0 }, name: "Stage 3: Novice" },
            4: { gridSize: 6, shipConfig: { 2: 1, 3: 1, 4: 1, 5: 0 }, name: "Stage 4: Amateur" },
            5: { gridSize: 7, shipConfig: { 2: 1, 3: 2, 4: 1, 5: 0 }, name: "Stage 5: Intermediate" },
            6: { gridSize: 8, shipConfig: { 2: 2, 3: 2, 4: 0, 5: 1 }, name: "Stage 6: Advanced" },
            7: { gridSize: 9, shipConfig: { 2: 2, 3: 2, 4: 1, 5: 1 }, name: "Stage 7: Expert" },
            8: { gridSize: 10, shipConfig: { 2: 2, 3: 2, 4: 2, 5: 1 }, name: "Stage 8: Master" },
            9: { gridSize: 10, shipConfig: { 2: 1, 3: 2, 4: 2, 5: 1 }, name: "Stage 9: Elite" },
            10: { gridSize: 10, shipConfig: { 2: 2, 3: 1, 4: 2, 5: 1 }, name: "Stage 10+: Admiral" },
            11: { gridSize: 10, shipConfig: { 2: 2, 3: 1, 4: 1, 5: 1 }, name: "Stage 11: Admiral+" }
        };
    }

    // Toggle between two rotation states: 0° (square) and 45° (diamond)
    rotateGrid = () => {
        this.setState((prev) => {
            const current = prev.gridRotationDeg || 0;
            const next = current === 45 ? 0 : 45;
            return { gridRotationDeg: next };
        });
    }

    // Device detection method - Compatible avec tous les navigateurs mobiles
    getDeviceInfo = () => {
    // Détection mobile uniquement via user agent
    const userAgent = navigator.userAgent || '';
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet|SamsungBrowser|EdgiOS|CriOS|FxiOS|OPiOS|mercury/i.test(userAgent);
    return { isMobile, isTouchDevice: false };
    }

    componentDidMount() {
        // Polyfills et correctifs pour la compatibilité navigateur
        this.initBrowserCompatibility();
        
        // Initialize viewport based on current state
        this.adjustMobileViewport(this.state.started);
    }

    // Initialisation des correctifs de compatibilité navigateur
    initBrowserCompatibility = () => {
        try {
            // Polyfill pour Array.prototype.includes (Safari plus ancien)
            if (!Array.prototype.includes) {
                // eslint-disable-next-line no-extend-native
                Array.prototype.includes = function(searchElement, fromIndex) {
                    return this.indexOf(searchElement, fromIndex) !== -1;
                };
            }

            // Polyfill pour Object.assign (IE/Edge plus ancien)
            if (typeof Object.assign !== 'function') {
                Object.assign = function(target) {
                    if (target == null) {
                        throw new TypeError('Cannot convert undefined or null to object');
                    }
                    const to = Object(target);
                    for (let index = 1; index < arguments.length; index++) {
                        const nextSource = arguments[index];
                        if (nextSource != null) {
                            for (const nextKey in nextSource) {
                                if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
                                    to[nextKey] = nextSource[nextKey];
                                }
                            }
                        }
                    }
                    return to;
                };
            }

            // Polyfill pour String.prototype.includes (Safari plus ancien)
            if (!String.prototype.includes) {
                // eslint-disable-next-line no-extend-native
                String.prototype.includes = function(search, start) {
                    if (typeof start !== 'number') {
                        start = 0;
                    }
                    if (start + search.length > this.length) {
                        return false;
                    } else {
                        return this.indexOf(search, start) !== -1;
                    }
                };
            }

            // Correctif pour les événements tactiles sur Safari
            if ('ontouchstart' in window) {
                document.addEventListener('touchstart', function() {}, { passive: true });
            }

            // Correctif pour le viewport sur iOS Safari
            const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent);
            if (isIOSSafari) {
                // Empêcher le zoom automatique sur les inputs
                const viewport = document.querySelector('meta[name="viewport"]');
                if (viewport) {
                    viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
                }
            }

        } catch (error) {
            console.warn('Browser compatibility initialization failed:', error);
        }
    }

    handleStart = () => {
        // Launch with Stage 11+ configuration
        const preset = this.presets[11];
        this.setState({
            gridSize: preset.gridSize,
            shipConfig: preset.shipConfig,
            started: true,
            showConfig: false,
            showPresets: false
        }, () => {
            // After state is updated, adjust viewport for mobile game view
            this.adjustMobileViewport(true);
        });
    }

    // Start the game using the current custom configuration in state
    startWithCurrentConfig = () => {
        this.setState({
            started: true,
            showConfig: false,
            showPresets: false
        }, () => {
            // After state is updated, adjust viewport for mobile game view
            this.adjustMobileViewport(true);
        });
    }

    // Dynamic viewport management pour tous les navigateurs mobiles
    adjustMobileViewport = (isGameView) => {
        try {
            const deviceInfo = this.getDeviceInfo();
            if (deviceInfo.isMobile) {
                const viewport = document.querySelector('meta[name="viewport"]');
                if (viewport) {
                    // Détection spécifique du navigateur pour des ajustements ciblés
                    const userAgent = navigator.userAgent || '';
                    const isSafari = /Safari/i.test(userAgent) && !/Chrome|CriOS|FxiOS|OPiOS/i.test(userAgent);
                    const isSamsungInternet = /SamsungBrowser/i.test(userAgent);
                    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
                    
                    if (isGameView) {
                        let viewportContent;
                        
                        if (isSafari && isIOSDevice) {
                            // Optimisation spéciale pour Safari iOS
                            viewportContent = 'width=1200, initial-scale=0.35, minimum-scale=0.1, maximum-scale=5.0, user-scalable=yes, viewport-fit=cover';
                        } else if (isSamsungInternet) {
                            // Optimisation pour Samsung Internet
                            viewportContent = 'width=1200, initial-scale=0.4, minimum-scale=0.2, maximum-scale=3.0, user-scalable=yes';
                        } else {
                            // Configuration par défaut pour les autres navigateurs
                            viewportContent = 'width=1200, initial-scale=0.35, minimum-scale=0.2, maximum-scale=3.0, user-scalable=yes';
                        }
                        
                        viewport.setAttribute('content', viewportContent);
                        
                        // Forcer le redimensionnement après un court délai pour certains navigateurs
                        setTimeout(() => {
                            try {
                                if (window.visualViewport && window.visualViewport.scale) {
                                    // Utiliser Visual Viewport API si disponible (Safari moderne)
                                    window.scrollTo(0, 0);
                                }
                            } catch (e) {
                                // Fallback silencieux
                            }
                        }, 100);
                        
                    } else {
                        // Normal zoom pour la page d'accueil
                        let viewportContent;
                        
                        if (isSafari && isIOSDevice) {
                            viewportContent = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
                        } else {
                            viewportContent = 'width=device-width, initial-scale=1';
                        }
                        
                        viewport.setAttribute('content', viewportContent);
                    }
                }
                
                // Correctif supplémentaire pour l'affichage sur certains navigateurs
                if (isGameView) {
                    // Empêcher le zoom accidentel sur double-tap (iOS Safari)
                    document.documentElement.style.touchAction = 'manipulation';
                }
            }
        } catch (error) {
            console.warn('Viewport adjustment failed:', error);
        }
    }

    handleGridConfig = () => {
        this.setState({ showConfig: !this.state.showConfig, showPresets: false });
    }

    handlePresets = () => {
        this.setState({ showPresets: !this.state.showPresets, showConfig: false });
    }

    selectPreset = (stage) => {
        const preset = this.presets[stage];
        this.setState({
            gridSize: preset.gridSize,
            shipConfig: preset.shipConfig,
            showPresets: false,
            started: true,
            currentStage: parseInt(stage)
        }, () => {
            // After state is updated, adjust viewport for mobile game view
            this.adjustMobileViewport(true);
        });
    }

    updateShipConfig = (size, count) => {
        this.setState({
            shipConfig: {
                ...this.state.shipConfig,
                [size]: Math.max(0, count)
            }
        });
    }

    showTutorial = () => {
        // Initialize interactive tutorial grid (3x3) with a hidden 2-length ship
        const gridSize = 3;
        const grid = {};
        for (let y = 0; y < gridSize; y++) {
            for (let x = 0; x < gridSize; x++) {
                grid[coordsToKey(x, y)] = null;
            }
        }

        // Place a 2-long ship in the middle row for the example (cells [0,1] and [1,1])
        const shipCells = [[0,1],[1,1]];

        this.setState({ tutorialVisible: true, tutorialStep: 1, tutorialGrid: grid, tutorialGridSize: gridSize, tutorialShipCells: shipCells, tutorialProbs: {}, tutorialScores: {} }, () => {
            // Compute initial display scores and best
            const scores = calculateDisplayScores(grid, gridSize, {2:1}, null);
            // Map scores to tutorialScores object keyed by coords
            const scoresMap = {};
            const probsMap = {};
            for (let y = 0; y < gridSize; y++) for (let x = 0; x < gridSize; x++) {
                const k = coordsToKey(x,y);
                const idx = y * gridSize + x;
                scoresMap[k] = scores[idx] || 0;
                probsMap[k] = 0;
            }
            this.setState({ tutorialScores: scoresMap, tutorialProbs: probsMap }, () => this.tutorialComputeBest());
        });
    }

    hideTutorial = () => {
        this.setState({ tutorialVisible: false, tutorialGrid: null, tutorialStep: 0, tutorialShipCells: [] });
    }

    // Helper: find connected HIT cells in the tutorial grid (4-connected)
    tutorialFindConnectedHitCells(grid, startX, startY) {
        const cells = [];
        const stack = [[startX, startY]];
        const visited = new Set();
        const gridSize = this.state.tutorialGridSize || 3;

        while (stack.length > 0) {
            const [x, y] = stack.pop();
            const pos = `${x},${y}`;
            if (visited.has(pos) || x < 0 || x >= gridSize || y < 0 || y >= gridSize) continue;
            const key = coordsToKey(x, y);
            if (grid[key] !== 'HIT') continue;
            visited.add(pos);
            cells.push([x, y]);
            stack.push([x+1,y],[x-1,y],[x,y+1],[x,y-1]);
        }

        return cells;
    }

    // Cycle tile state in tutorial: null -> MISS -> HIT
    tutorialOnClick(x, y) {
        const grid = { ...(this.state.tutorialGrid || {}) };
        const key = coordsToKey(x, y);
        const current = grid[key];
        // Mirror main grid cycle: MISS -> HIT -> null
        if (current === 'MISS') {
            grid[key] = 'HIT';
        } else if (current === 'HIT') {
            grid[key] = null;
        } else {
            // null or undefined -> MISS
            grid[key] = 'MISS';
        }

        this.setState({ tutorialGrid: grid }, () => {
            // Recompute the tutorial recommended tile after grid change
            this.tutorialComputeBest();
            // If two adjacent hits appear, ensure step advances or show confirmation
            const { tutorialGridSize } = this.state;
            const hitGroups = [];
            const visited = new Set();
            for (let yy = 0; yy < tutorialGridSize; yy++) {
                for (let xx = 0; xx < tutorialGridSize; xx++) {
                    const pos = `${xx},${yy}`;
                    const k = coordsToKey(xx, yy);
                    if (visited.has(pos) || grid[k] !== 'HIT') continue;
                    const group = this.tutorialFindConnectedHitCells(grid, xx, yy);
                    group.forEach(c => visited.add(`${c[0]},${c[1]}`));
                    if (group.length >= 2) hitGroups.push(group);
                }
            }

            if (hitGroups.length > 0) {
                // Keep one popup group for confirmation
                this.setState({ tutorialHitGroup: hitGroups[0] }, () => {
                    // Auto-advance to step 4 when a hit group is detected
                    if (this.state.tutorialStep < 4) this.setState({ tutorialStep: 4 });
                });
            } else {
                this.setState({ tutorialHitGroup: null });
            }

            // Auto-advance steps: if user has placed a MISS anywhere and step is 1, go to 2
            if (this.state.tutorialStep === 1) {
                const anyMiss = Object.values(grid).some(v => v === 'MISS');
                if (anyMiss) this.setState({ tutorialStep: 2 });
            }
            // If there's at least one HIT and step is 2, advance to 3
            if (this.state.tutorialStep === 2) {
                const anyHit = Object.values(grid).some(v => v === 'HIT');
                if (anyHit) this.setState({ tutorialStep: 3 });
            }
        });
    }

    tutorialComputeBest() {
        const grid = this.state.tutorialGrid || {};
        const gs = this.state.tutorialGridSize || 3;

        // Try to use the main probability-based method for consistency
        try {
            const shipConfig = { 2: 1 }; // tutorial has a single 2-long ship
            const shipsDestroyed = 0;
            const best = calculateBestMoveProbability(grid, gs, shipConfig, shipsDestroyed, null);
            if (best && Array.isArray(best)) {
                this.setState({ tutorialBest: best });
                return;
            }
        } catch (e) {
            // if anything goes wrong, fall back to a simple heuristic below
            console.warn('Tutorial best computation fallback:', e);
        }

        // Fallback: prefer empty adjacent to HIT, else center
        let best = null;
        for (let y = 0; y < gs; y++) {
            for (let x = 0; x < gs; x++) {
                if (grid[coordsToKey(x, y)] === 'HIT') {
                    const neighbors = [[x+1,y],[x-1,y],[x,y+1],[x,y-1]];
                    for (const [nx, ny] of neighbors) {
                        if (nx < 0 || ny < 0 || nx >= gs || ny >= gs) continue;
                        const k = coordsToKey(nx, ny);
                        if (grid[k] === null || grid[k] === undefined) {
                            best = [nx, ny];
                            break;
                        }
                    }
                }
                if (best) break;
            }
            if (best) break;
        }

        if (!best) {
            const center = Math.floor(gs/2);
            const cKey = coordsToKey(center, center);
            if (grid[cKey] === null || grid[cKey] === undefined) best = [center, center];
        }

        this.setState({ tutorialBest: best });
    }

    tutorialConfirmSunk(confirm) {
        const grid = { ...(this.state.tutorialGrid || {}) };
        const group = this.state.tutorialHitGroup || [];
        if (confirm && group.length > 0) {
            for (const [x,y] of group) {
                grid[coordsToKey(x,y)] = 'SUNK';
            }
            // Optionally mark surrounding cells as DEDUCTED_MISS for realism
            for (const [x,y] of group) {
                for (let dx=-1; dx<=1; dx++) for (let dy=-1; dy<=1; dy++) {
                    const nx = x+dx, ny = y+dy;
                    if (nx < 0 || ny < 0 || nx >= this.state.tutorialGridSize || ny >= this.state.tutorialGridSize) continue;
                    const k = coordsToKey(nx, ny);
                    if (grid[k] === null) grid[k] = 'DEDUCTED_MISS';
                }
            }
        }

        // Recompute tutorial scores and best after confirmation
        const gs = this.state.tutorialGridSize || 3;
        const scores = calculateDisplayScores(grid, gs, {2:1}, null);
        const scoresMap = {};
        const probsMap = {};
        for (let y = 0; y < gs; y++) for (let x = 0; x < gs; x++) {
            const k = coordsToKey(x,y);
            const idx = y * gs + x;
            scoresMap[k] = scores[idx] || 0;
            probsMap[k] = 0;
        }

        this.setState({ tutorialGrid: grid, tutorialHitGroup: null, tutorialScores: scoresMap, tutorialProbs: probsMap }, () => {
            this.tutorialComputeBest();
            // Auto-advance to final step after confirmation
            this.setState({ tutorialStep: 4 });
        });
    }

    tutorialPrevStep() {
        const s = Math.max(1, (this.state.tutorialStep || 1) - 1);
        this.setState({ tutorialStep: s });
    }

    tutorialNextStep() {
        const s = Math.min(4, (this.state.tutorialStep || 1) + 1);
        this.setState({ tutorialStep: s });
    }

    renderTutorialModal() {
        if (!this.state.tutorialVisible) return null;
        const grid = this.state.tutorialGrid || {};
        const gridSize = this.state.tutorialGridSize || 3;
        const hitGroup = this.state.tutorialHitGroup || null;

        return (
            <div className="tutorial-backdrop" onClick={this.hideTutorial}>
                <div className="tutorial-modal" onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div>
                            <div className="tutorial-title">Interactive Tutorial</div>
                           
                        </div>
                        
                    </div>

                    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                        <div style={{ flex: '1 1 0' }}>
                            <h4>Instruction</h4>
                            

                            <ol className="tutorial-list" style={{ marginTop: '12px' }}>
                                <li>Click a tile to mark it as a MISS.</li>
                                <li>Click the same tile again to mark it as a HIT.</li>
                                <li>Click an adjacent tile to make a second HIT.</li>
                                <li>Confirm the sunk ship when the popup appears.</li>
                            </ol>
                        </div>

                        <div style={{ width: '220px' }}>
                            <div style={{ textAlign: 'center', marginBottom: '8px' }}><strong>Example 3×3 grid</strong></div>
                            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${gridSize}, 56px)`, gap: '6px', justifyContent: 'center' }}>
                                {Array.from({ length: gridSize }).map((_, y) => (
                                    Array.from({ length: gridSize }).map((__, x) => {
                                        const key = coordsToKey(x, y);
                                        const text = grid[key];
                                        return (
                                            <Tile
                                                key={`tut_${x}_${y}`}
                                                x={x}
                                                y={y}
                                                text={text}
                                                score={this.state.tutorialScores ? this.state.tutorialScores[key] : 0}
                                                valid={true}
                                                fontSize={'16px'}
                                                deviceInfo={{ isMobile: false, isTouchDevice: false }}
                                                best={this.state.tutorialBest}
                                                onClick={() => this.tutorialOnClick(x, y)}
                                                tutorial={true}
                                                minScore={0}
                                                maxScore={0}
                                                prob={this.state.tutorialProbs ? this.state.tutorialProbs[key] : 0}
                                            />
                                        );
                                    })
                                ))}
                            </div>

                            {hitGroup && (
                                <div className="ship-confirmation-container" style={{ marginTop: '12px' }}>
                                    <div className="ship-confirmation-popup" style={{ padding: '8px' }}>
                                        <span className="popup-text">⚓ Is ship at {hitGroup.map(([x,y]) => `${String.fromCharCode(65 + x)}${y+1}`).join('-')} sunk?</span>
                                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                            <button className="popup-confirm-btn" onClick={() => this.tutorialConfirmSunk(true)}>✓ YES!</button>
                                            <button className="popup-cancel-btn" onClick={() => this.tutorialConfirmSunk(false)}>No</button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="tutorial-close-row" style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button className="btn tutorial-close-btn" onClick={this.hideTutorial}>Close</button>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        // Device detection pour toute la méthode de rendu - Version robuste
        const deviceInfo = this.getDeviceInfo();
        const { isMobile, isTouchDevice } = deviceInfo;
        
        if (!this.state.started) {
            return (
                <div className={`App ${isMobile ? 'mobile-layout' : 'desktop-layout'} ${isTouchDevice ? 'touch-device' : 'mouse-device'}`}>
                    <h1 className={isMobile ? 'mobile-title' : 'desktop-title'}>
                        Sonar Ops Helper
                    </h1>
                    <div className={isMobile ? 'mobile-menu-buttons' : 'menu-buttons'}>
                        <button className="frontpage-btn" onClick={this.handleStart}>
                            {isMobile ? '🎯 ' : ''}Stage 11+
                        </button>
                        <button className="frontpage-btn" onClick={this.handlePresets}>
                            {isMobile ? '📋 ' : ''}Choose Preset Stage
                        </button>
                        <button className="frontpage-btn" onClick={this.handleGridConfig}>
                            {isMobile ? '⚙️ ' : ''}Custom Configuration
                        </button>
                        <button className="frontpage-btn" onClick={this.showTutorial}>
                            {isMobile ? '❓ ' : ''}Tutorial
                        </button>
                    </div>
                    
                    {this.state.showPresets && (
                        <div className={isMobile ? 'mobile-presets-container' : 'presets-container'}>
                            <h3>Select a Challenge Stage</h3>
                            <div className={isMobile ? 'mobile-presets-grid' : 'presets-grid'}>
                                {Object.entries(this.presets).map(([stage, preset]) => (
                                    <div key={stage} className={isMobile ? 'mobile-preset-card' : 'preset-card'} onClick={() => this.selectPreset(stage)}>
                                        <h4>{preset.name}</h4>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {this.state.showConfig && (
                        <div className={isMobile ? 'mobile-config-container' : 'config-container'}>
                            <h3>Custom Configuration</h3>
                            <div className={isMobile ? 'mobile-config-item' : ''}>
                                <label>Grid Size: </label>
                                <input 
                                    type="number" 
                                    min="4" 
                                    max="10" 
                                    value={this.state.gridSize}
                                    onChange={(e) => this.setState({ gridSize: parseInt(e.target.value) })}
                                />
                            </div>
                            <h4>Ship Configuration:</h4>
                            {[2, 3, 4, 5].map(size => (
                                <div key={size} className={isMobile ? 'mobile-config-item' : ''}>
                                    <label>Ships of size {size}: </label>
                                    <input 
                                        type="number" 
                                        min="0" 
                                        max="5"
                                        value={this.state.shipConfig[size] || 0}
                                        onChange={(e) => this.updateShipConfig(size, parseInt(e.target.value) || 0)}
                                    />
                                </div>
                            ))}
                            <button 
                                className={isMobile ? 'mobile-btn primary' : ''}
                                onClick={this.startWithCurrentConfig}
                            >
                                Start with This Configuration
                            </button>
                        </div>
                    )}
                    
                    <KofiFooter deviceInfo={deviceInfo} />
                    {this.renderTutorialModal()}
                </div>
            );
        }

        const totalShips = Object.values(this.state.shipConfig).reduce((a, b) => a + b, 0);
        
        return (
            <div className={`App ${isMobile ? 'mobile-layout game-mobile' : 'desktop-layout game-desktop'} ${isTouchDevice ? 'touch-device' : 'mouse-device'}`}>
                <div className={isMobile ? 'mobile-game-header' : 'desktop-game-header'} style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <h1 className={isMobile ? 'mobile-title' : 'desktop-title'}>
                        {isMobile ? 'Sonar Ops' : 'Sonar Ops Helper'}
                    </h1>
                    <p className={isMobile ? 'mobile-config-info' : 'desktop-config-info'}>
                        {isMobile ? (
                            <>
                                {this.state.gridSize}x{this.state.gridSize} grid<br/>
                                {totalShips} ships total
                            </>
                        ) : (
                            <>
                                Current Configuration: {this.state.gridSize}x{this.state.gridSize} grid, 
                                {Object.entries(this.state.shipConfig)
                                    .filter(([size, count]) => count > 0)
                                    .map(([size, count]) => `${count}x${size}`)
                                    .join(', ')} 
                                ({totalShips} total ships)
                            </>
                        )}
                    </p>
                    <button 
                        className="change-config-button"
                        onClick={() => this.setState({ started: false })}
                    >
                        {isMobile ? '⚙️ ' : ''}Change Configuration
                    </button>
                    <button
                        className="change-config-button"
                        onClick={this.rotateGrid}
                        style={{ marginLeft: '8px' }}
                        title="Rotate grid by 45°"
                    >
                        🔁 {isMobile ? '' : `Rotate ${this.state.gridRotationDeg || 0}°`}
                    </button>
                    <button 
                        className="change-config-button"
                        onClick={this.showTutorial}
                        style={{ marginLeft: '8px' }}
                    >
                        {isMobile ? '❓ ' : ''}Help
                    </button>
                </div>
                
                {/* MainMap elements rendered directly without wrapper container */}
                <MainMap 
                    gridSize={this.state.gridSize} 
                    shipConfig={this.state.shipConfig}
                    deviceInfo={deviceInfo}
                    currentStage={this.state.currentStage}
                    onStageChange={(stage) => {
                        const preset = this.presets[stage];
                        this.setState({
                            gridSize: preset.gridSize,
                            shipConfig: preset.shipConfig,
                            currentStage: stage
                        }, () => {
                            // Reset the grid after changing stage
                            if (this.mainMapRef) {
                                this.mainMapRef.clearField();
                            }
                            // Get a reference to the MainMap component and call clearField
                            if (this.mainMapRef && this.mainMapRef.clearField) {
                                this.mainMapRef.clearField();
                            }
                        });
                    }}
                    rotation={this.state.gridRotationDeg}
                    ref={ref => this.mainMapRef = ref}
                />
                
                <span className={isMobile ? 'mobile-version' : 'desktop-version'}>{VERSION_STRING}</span>
                
                <KofiFooter deviceInfo={deviceInfo} />
                {this.renderTutorialModal()}
            </div>
        );
    }
}

export default App;
